import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { app } from "@microsoft/teams-js";
import { debounce } from 'lodash';
import { Icon } from '@fluentui/react';
import { Avatar, Button, Dialog, Flex, FlexItem, Image, Grid, MenuButton, Popup, Pill, Text, Tooltip, TextArea } from "@fluentui/react-northstar";
import { CloseIcon, ExclamationTriangleIcon, MoreIcon, CallIcon, MentionIcon, ShieldKeyholeIcon, ChatIcon } from '@fluentui/react-icons-northstar';
import { withTranslation, WithTranslation } from "react-i18next";
import { DateTimeParser } from "../../../helpers/date-time";
import { LoaderInfinite } from "../../loaders/loaders";
import StatusQueueConstants from "../../../constants/status-queue";
import Roles from "../../../constants/roles";

interface IListagemIniciadoProps extends WithTranslation {
    item: any,
    status: string,
    getLabelStatusLocale: Function,
    onCancelCall: Function,
    authEnabled: boolean,
    govBrEnabled: boolean,
    aadId: string;
    finalizar: Function,
    internalAccessEnabled: boolean,
    role: string,
    isAdmin: boolean,
    pageSize: number,
    loadingInfinite: boolean,
    isTeamContext?: boolean,
    onViewHistory: Function,
    endQueueCommentsEnabled?: boolean,
}

const ListagemIniciado: React.FunctionComponent<IListagemIniciadoProps> = props => {
    const localize = props.t;

    const [isValidateEndQueue, validateEndQueue] = useState<boolean | null>(null);
    const [currentQueue, setCurrentQueue] = useState<any>();
    const [confirm, setConfirm] = useState<boolean | undefined>(undefined);
    const [justifyEndQueue, setJustifyEndQueue] = useState<boolean | undefined>(undefined);
    const [justificationValue, setJustificationEndQueueValue] = useState<string>();
    const [currentQueueWithJustification, setCurrentQueueWithJustification] = useState<any>();

    const endQueue = (item: any, confirmParam?: boolean) => {
        setCurrentQueue(item);
        setConfirm(confirmParam);
    }

    const endQueueWithJustification = () => {
        currentQueueWithJustification.queue.loadingEndMeeting = true;

        if (justificationValue) {
            currentQueueWithJustification.queue.justification = justificationValue;
        }

        props.finalizar(currentQueueWithJustification.queue);
    }

    const onCancelValidateEndQueue = () => {
        validateEndQueue(true);
    }

    const debouncedSetValue = useCallback(
        debounce((value) => setJustificationEndQueueValue(value), 100),
        []
    );

    useEffect(() => {
        if (currentQueue) {
            currentQueue.queue.loadingEndMeeting = true

            validateEndQueue(true);

            if (currentQueue.queue.atendenteId != props.aadId && !confirm) {
                currentQueue.queue.loadingEndMeeting = false;

                validateEndQueue(false);

                return;
            }

            props.finalizar(currentQueue.queue);
        }
    }, [currentQueue, confirm]);

    return (
        <Flex fill column>
            {!props.item.length ?
                <Flex hAlign="center" gap="gap.medium" styles={{ marginTop: '60px' }}>
                    <ExclamationTriangleIcon outline />
                    <Text size="small" weight="light" content={`${localize("NaoExistem")} atendimentos iniciados ${localize("NestaUnidade")}`} />
                </Flex>
                :
                <Grid
                    columns={((props.authEnabled && props.govBrEnabled) || props.internalAccessEnabled) ?
                        'minmax(200px, 280px) minmax(70px, 70px) minmax(120px, 150px) 0.6fr 1fr 0.6fr 0.6fr' :
                        'minmax(200px, 280px) minmax(70px, 70px) minmax(120px, 150px) 1fr 0.6fr 0.6fr'
                    }>
                    <Flex gap="gap.small" className="header">
                        <FlexItem>
                            <Text content={localize("StatusAtendimento")} className="margin-style header-nome-serventia" />
                        </FlexItem>
                        <FlexItem>
                            <Tooltip
                                trigger={<Icon className="margin-style info-icon" iconName="Info" />}
                                content={
                                    <React.Fragment>
                                        <Text content={`\u25CF ${localize("UsuarioNaFila")}`} />
                                        <br /><br />
                                        <Text content={`\u25CF ${localize("AtendimentoConfirmado")}`} />
                                        <br /><br />
                                        <Text content={`\u25CF ${localize("UsuarioEntrou")}`} />
                                    </React.Fragment>
                                }
                            />
                        </FlexItem>
                    </Flex>

                    <Flex gap="gap.small" className="header">

                    </Flex>

                    <Flex gap="gap.small" className="header">
                        <FlexItem>
                            <Text className="margin-style" content={localize("IniciadoEm")} />
                        </FlexItem>
                        <FlexItem>
                            <Tooltip trigger={<Icon className="margin-style info-icon" iconName="Info" />} content={localize("HorarioSolicitacaoIniciada")} />
                        </FlexItem>
                    </Flex>

                    {((props.authEnabled && props.govBrEnabled) || props.internalAccessEnabled) &&
                        <Flex gap="gap.small" className="header">
                            <FlexItem>
                                <Text className="margin-style" content={`Solicitante`} />
                            </FlexItem>
                        </Flex>
                    }

                    <Flex gap="gap.small" className="header">
                        <FlexItem>
                            <Text className="margin-style" content={!props.isTeamContext ? localize("Assunto") : "Tag"} />
                        </FlexItem>
                        <FlexItem>
                            <Tooltip trigger={<Icon className="margin-style info-icon" iconName="Info" />} content={localize("AssuntoAtendimento")} />
                        </FlexItem>
                    </Flex>

                    <Flex gap="gap.small" className="header">
                        <FlexItem>
                            <Text className="margin-style" content={localize("Atendimento")} />
                        </FlexItem>
                        <FlexItem>
                            <Tooltip trigger={<Icon className="margin-style info-icon" iconName="Info" />} content={localize("InformacoesAtendente")} />
                        </FlexItem>
                    </Flex>

                    <Flex gap="gap.small" className="header">
                        <FlexItem>
                            <Text className="margin-style" content={localize("Atendente")} />
                        </FlexItem>
                        <FlexItem>
                            <Tooltip trigger={<Icon className="margin-style info-icon" iconName="Info" />} content={localize("Atendente")} />
                        </FlexItem>
                    </Flex>

                    {props.item.map((item: any, index: number) => {
                        const queue = item?.queue;
                        const chatId = item?.queue.OnlineMeetingData?.chatInfo?.threadId ?? null;

                        let queueId = `#${queue?.id.substr(queue?.id.length - 6)} ${props.getLabelStatusLocale(queue?.status)}`; 
                        let prefix = "#";

                        if (props.isTeamContext) {
                            prefix = !queue.AdditionalData?.prefix ? prefix : queue.AdditionalData.prefix;
                        }

                        if (queue.AdditionalData?.nrProcesso) {
                            queueId = `${queue.AdditionalData?.nrProcesso} ${props.getLabelStatusLocale(queue?.status)}`;
                        }

                        return (
                            <React.Fragment key={index}>
                                <Flex gap="gap.medium" vAlign="center" className={queue?.isSelected ? "row row-active" : "row"}>
                                    {!props.isTeamContext ?
                                        <FlexItem>
                                            <Text
                                                onClick={() => props.onViewHistory(queue?.id, queue?.Ancora)}
                                                styles={{ cursor: "pointer", marginLeft: '3.5rem' }}
                                                className="text-ms-color"
                                                content={queueId}
                                            />
                                        </FlexItem>
                                        :
                                        <Tooltip
                                            trigger={
                                                <Flex gap="gap.small" vAlign="center">
                                                    <Text
                                                        onClick={() => props.onViewHistory(queue?.id, queue?.Ancora)}
                                                        styles={{ cursor: "pointer", marginLeft: '3.5rem' }}
                                                        className="text-ms-color"
                                                        content={`${prefix}${queue?.id.substr(queue?.id.length - 6)} ${props.getLabelStatusLocale(queue?.status)}`}
                                                    />
                                                    {DateTimeParser.dateOffsetParser(queue.created).format("DD/MM/YYYY") != DateTimeParser.dateOffsetParser(new Date).format("DD/MM/YYYY") &&
                                                        <Pill
                                                            size="small"
                                                            appearance="outline"
                                                            className="date-pill"
                                                            content={DateTimeParser.dateOffsetParser(queue.created).format("DD/MM/YYYY")}
                                                        />
                                                    }
                                                </Flex>
                                            }
                                            content={`Unidade: ${queue?.nome}`}
                                            position="after"
                                            align="center"
                                        />
                                    }
                                </Flex>

                                {!props.isAdmin && props.role == Roles.leitor ?
                                    <Flex className={queue?.isSelected ? "row row-active" : "row"} />
                                    :
                                    <Flex gap="gap.medium" vAlign="center" className={queue?.isSelected ? "row row-active" : "row"}>
                                        <FlexItem>
                                            <MenuButton
                                                tabIndex={-1}
                                                trigger={<Button icon={<MoreIcon />} iconOnly text />}
                                                on="click"
                                                menu={[
                                                    {
                                                        key: 'join',
                                                        content: localize("EntrarAtendimento"),
                                                        icon: <CallIcon outline />,
                                                        onClick: () => {
                                                            const url = window.location.ancestorOrigins[0].includes("cloud") ?
                                                                queue?.meetingUrl.replace("https://teams.microsoft.com", "https://teams.cloud.microsoft") :
                                                                queue?.meetingUrl;

                                                            app.openLink(url);
                                                        }   
                                                    },
                                                    {
                                                        key: 'joinChat',
                                                        content: localize("EntrarChat"),
                                                        icon: <ChatIcon outline />,
                                                        onClick: () => {
                                                            const url = window.location.ancestorOrigins[0].includes("cloud") ?
                                                                `https://teams.cloud.microsoft/l/chat/${chatId}` : `https://teams.microsoft.com/l/chat/${chatId}`;

                                                            app.openLink(url);
                                                        },
                                                        hidden: chatId == null
                                                    },
                                                    {
                                                        key: 'cancel',
                                                        content: localize("Cancelar"),
                                                        icon: <CloseIcon outline />,
                                                        onClick: () => props.onCancelCall(queue?.id),
                                                        hidden: queue.bookingsId || queue.status == StatusQueueConstants.emAtendimento
                                                    },
                                                ]}
                                            />
                                        </FlexItem>
                                    </Flex>
                                }

                                <Flex gap="gap.medium" vAlign="center" className={props.item?.isSelected ? "row row-active" : "row"}>
                                    <FlexItem>
                                        <Text
                                            content={
                                                queue?.Schedule ?
                                                DateTimeParser.dateOffsetParser(queue?.Schedule.date).format("HH:mm") :
                                                DateTimeParser.dateOffsetParser(queue?.created).format("HH:mm")
                                            }
                                        />
                                    </FlexItem>
                                </Flex>

                                {((props.authEnabled && props.govBrEnabled) || props.internalAccessEnabled) &&
                                    <Flex gap="gap.medium" vAlign="center" className="row">
                                        {queue?.UserProfile != null &&
                                            <Popup
                                                mouseLeaveDelay={0}
                                                trigger={
                                                    <Flex gap="gap.small" vAlign="center">
                                                        {(queue?.UserProfile?.userPhoto != null && queue?.UserProfile?.userPhoto != "") ?
                                                            <Avatar
                                                                image={`data:image/jpeg;base64,${queue?.UserProfile?.userPhoto}`}
                                                                size="small"
                                                            />
                                                            :
                                                            <Flex gap="gap.smaller">
                                                                <Avatar size="small" name={queue?.UserProfile?.userName == "" ? queue?.UserProfile?.userLogin?.toUpperCase() : queue?.UserProfile?.userName} />
                                                            </Flex>
                                                        }

                                                        <Text
                                                            truncated
                                                            content={queue?.UserProfile?.userName == "" ? queue?.UserProfile?.userLogin?.split("@")[0] : queue?.UserProfile?.userName?.split(" ")[0]}
                                                        />
                                                    </Flex>
                                                }
                                                on="hover"
                                                content={
                                                    <Flex gap="gap.medium" vAlign="center" column>
                                                        <Flex gap="gap.medium" vAlign="center">
                                                            {(queue?.UserProfile?.userPhoto != null && queue?.UserProfile?.userPhoto != "") ?
                                                                <Flex hAlign="center" gap="gap.small" column>
                                                                    <Avatar
                                                                        image={`data:image/jpeg;base64,${queue?.UserProfile?.userPhoto}`}
                                                                        size="larger"
                                                                    />
                                                                    <Image width="45" src="https://studio365cdn.azureedge.net/balcaovirtual/gov-br.svg" />
                                                                </Flex>
                                                                :
                                                                <Flex gap="gap.smaller">
                                                                    <Avatar size="small" name={queue?.UserProfile?.userName == "" ? queue?.UserProfile?.userLogin?.toUpperCase() : queue?.UserProfile?.userName} />
                                                                </Flex>
                                                            }

                                                            <Flex hAlign="start" gap="gap.smaller" column>
                                                                <Text weight="semibold" content={queue?.UserProfile?.userName} />

                                                                {queue?.UserProfile?.userLogin &&
                                                                    <Flex vAlign="center" gap="gap.smaller">
                                                                        <ShieldKeyholeIcon size="smaller" outline />
                                                                        <Text weight="light" size="smaller" content={queue?.UserProfile?.userLogin} />
                                                                    </Flex>
                                                                }

                                                                {queue?.UserProfile?.email &&
                                                                    <Flex vAlign="center" gap="gap.smaller">
                                                                        <MentionIcon size="smaller" outline />
                                                                        <Text weight="light" size="smaller" content={queue?.UserProfile?.email} />
                                                                    </Flex>
                                                                }

                                                                {queue?.UserProfile?.phoneNumber &&
                                                                    <Flex vAlign="center" gap="gap.smaller">
                                                                        <CallIcon size="smaller" outline />
                                                                        <Text weight="light" size="smaller" content={queue?.UserProfile?.phoneNumber} />
                                                                    </Flex>
                                                                }
                                                            </Flex>
                                                        </Flex>
                                                    </Flex>
                                                }
                                            />
                                        }
                                    </Flex>
                                }

                                <Flex gap="gap.medium" vAlign="center" className={queue?.isSelected ? "row row-active" : "row"}>
                                    <FlexItem>
                                        <Text content={queue?.assunto} />
                                    </FlexItem>
                                </Flex>

                                {!props.isAdmin && props.role == Roles.leitor ?
                                    <Flex className={queue?.isSelected ? "row row-active" : "row"} />
                                    :
                                    <Flex gap="gap.medium" vAlign="center" className={queue?.isSelected ? "row row-active" : "row"}>
                                        <Dialog
                                            key={index}
                                            open={isValidateEndQueue == false}
                                            cancelButton={localize("Cancelar")}
                                            confirmButton={localize("Confirmar")}
                                            onCancel={() => { onCancelValidateEndQueue() }}
                                            onConfirm={() => {
                                                endQueue(currentQueue, true)
                                            }}
                                            content={
                                                <Flex gap="gap.medium" column>
                                                    <Flex gap="gap.medium">
                                                        {currentQueue?.atendentePhoto != null && currentQueue?.atendentePhoto != "" ?
                                                            <Avatar
                                                                image={`data:image/jpeg;base64,${currentQueue?.atendentePhoto}`}
                                                                size="small"
                                                            />
                                                            :
                                                            <Flex gap="gap.smaller">
                                                                <Avatar size="small" name={currentQueue?.queue?.atendenteName} />
                                                            </Flex>
                                                        }

                                                        <Flex vAlign="center" gap="gap.large">
                                                            <Text content={currentQueue?.queue?.atendenteName} />
                                                        </Flex>
                                                    </Flex>

                                                    <Text content={localize("DesejaEncerrar")} />
                                                </Flex>
                                            }
                                            header={localize("AtendimentoAtribuido")}
                                        />

                                        {(props.isTeamContext || props.endQueueCommentsEnabled) &&
                                            <Dialog
                                                key={`teamContext-${index}`}
                                                open={justifyEndQueue == true}
                                                cancelButton={localize("Cancelar")}
                                                confirmButton={!justificationValue ? null : localize("Confirmar")}
                                                onConfirm={() => {
                                                    setJustifyEndQueue(false);
                                                    endQueueWithJustification();
                                                }}
                                                onCancel={() => { 
                                                    setJustifyEndQueue(false);
                                                    setCurrentQueueWithJustification(undefined);
                                                }}
                                                content={
                                                    <Flex gap="gap.medium" column>
                                                        <TextArea
                                                            variables={{
                                                                "height": "120px"
                                                            }}
                                                            fluid
                                                            onChange={(e, data) => debouncedSetValue(data.value)}
                                                            required
                                                        />
                                                    </Flex>
                                                }
                                                header={
                                                    <Flex gap="gap.smaller">
                                                        <Text content="Solução" />
                                                        <span style={{ color: "#ED1B23" }}>*</span>
                                                    </Flex>
                                                }
                                            /> 
                                        }
                                            
                                        <FlexItem>
                                            {!props.isTeamContext && !props.endQueueCommentsEnabled ?
                                                <Button
                                                    content={queue.status != StatusQueueConstants.emAtendimento ? localize("AguardandoUsuario") : localize("Encerrar")}
                                                    loading={queue.loadingEndMeeting}
                                                    disabled={queue.status != StatusQueueConstants.emAtendimento}
                                                    iconPosition="after"
                                                    onClick={() => endQueue(item)}
                                                    primary={queue.loadingEndMeeting ? false : true}
                                                    className={queue.loadingEndMeeting ? "readonly" : ""}
                                                />
                                                :
                                                <Button
                                                    content={queue.status != StatusQueueConstants.emAtendimento ? localize("AguardandoUsuario") : localize("Encerrar")}
                                                    loading={queue.loadingEndMeeting}
                                                    disabled={queue.status != StatusQueueConstants.emAtendimento}
                                                    iconPosition="after"
                                                    onClick={() => {
                                                        setJustifyEndQueue(true);
                                                        setCurrentQueueWithJustification(item);
                                                    }}
                                                    primary={queue.loadingEndMeeting ? false : true}
                                                    className={queue.loadingEndMeeting ? "readonly" : ""}
                                                />
                                            }
                                        </FlexItem>
                                    </Flex>
                                }

                                <Popup
                                    mouseLeaveDelay={0}
                                    trigger={
                                        <Flex gap="gap.smaller" vAlign="center" hAlign="start" className={queue?.isSelected ? "row row-active" : "row"}>
                                            {queue?.atendenteName &&
                                                <React.Fragment>
                                                    {(item?.atendentePhoto != null && item?.atendentePhoto != "") ?
                                                        <Avatar
                                                            image={`data:image/jpeg;base64,${item?.atendentePhoto}`}
                                                            size="small"
                                                        />
                                                        :
                                                        <Flex gap="gap.smaller">
                                                            <Avatar size="small" name={queue?.atendenteName} />
                                                        </Flex>
                                                    }

                                                    <Flex vAlign="center" gap="gap.large">
                                                        <Text content={queue?.atendenteName?.split(" ")[0]} />
                                                    </Flex>
                                                </React.Fragment>
                                            }
                                        </Flex>
                                    }
                                    on="hover"
                                    content={
                                        <Flex gap="gap.medium" vAlign="center" column>
                                            <Flex gap="gap.medium" vAlign="center">
                                                {queue?.atendenteName &&
                                                    <React.Fragment>
                                                        {(item?.atendentePhoto != null && item?.atendentePhoto != "") ?
                                                            <Avatar
                                                                image={`data:image/jpeg;base64,${item?.atendentePhoto}`}
                                                                size="small"
                                                            />
                                                            :
                                                            <Flex gap="gap.smaller">
                                                                <Avatar size="small" name={queue?.atendenteName} />
                                                            </Flex>
                                                        }

                                                        <Flex vAlign="center" gap="gap.large">
                                                            <Text content={queue?.atendenteName} />
                                                        </Flex>
                                                    </React.Fragment>
                                                }
                                            </Flex>
                                        </Flex>
                                    }
                                />
                            </React.Fragment>
                        );
                    })}
                </Grid>
            }

            {(props.loadingInfinite && props.item.length >= props.pageSize) && <LoaderInfinite />}
        </Flex>
    );
};

const listagemIniciadoWithTranslation = withTranslation()(ListagemIniciado);
export default listagemIniciadoWithTranslation;